/* oxlint-disable max-lines -- legacy file, split it before you add code */
import { Button } from '@askable/ui/core/button';
import { DropdownMenuItem } from '@askable/ui/core/dropdown-menu';
import { Lock, Plus } from 'lucide-react';
import { lazy, type ReactNode, Suspense, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'shared-utils';
import { useIsGuest } from 'src/hooks/useIsGuest';
import { holdWhileReloading } from 'src/lib/reload-on-chunk-error';

import { GuestLockedPopover } from 'components/common/GuestLockedPopover';
import { LockedPopoverGroup, useLockedPopoverGroup } from 'components/common/LockedPopover';
import { ResponsiveMenu } from 'components/common/MainNavSidebar/components/ResponsiveMenu';
import { useConnectedClient } from 'context/ConnectedClientContext';

import { AddToProjectDialog } from './AddToProjectDialog';
import { CreateStudyItem, UpgradePopover } from './CreateStudyItem';
import { useCreateStudyOptions } from './useCreateStudyOptions';

import type { StudyOption } from './CreateStudyItem';
import type { PopoverSide } from './useCreateStudyOptions';

// The sidebar renders this menu on every page, so the wizard (and its Lottie animation) loads in its own chunk,
// and only after its first open.
const AIModeratedSetup = lazy(() =>
  holdWhileReloading(() => import('components/AIModeratedSetup'))().then(m => ({ default: m.AIModeratedSetup })),
);

type Side = 'top' | 'right' | 'bottom' | 'left';
type CreateStudyProps = {
  trigger?: ReactNode;
  title?: string;
  isCollapsed?: boolean;
  align?: 'start' | 'center' | 'end';
  side?: Side;
  popoverSide?: PopoverSide;
  contentClassName?: string;
  projectId?: string;
};

const MenuLabel = ({ children }: { children: ReactNode }) => (
  <div className="px-3 pb-1 pt-2 text-sm text-muted-foreground">{children}</div>
);

type ContentProps = {
  studyOptions: StudyOption[];
  projectOptions: StudyOption[];
  hideProjectOptions?: boolean;
};

const DesktopCreateStudyContent = ({ studyOptions, projectOptions, hideProjectOptions }: ContentProps) => {
  const { t } = useTranslation();
  const group = useLockedPopoverGroup();

  return (
    <>
      <MenuLabel>{t('sections.createStudy.studies.title')}</MenuLabel>
      {studyOptions.map(option => {
        if (option.status === 'guest_locked') {
          return (
            <GuestLockedPopover
              key={option.title}
              description={t('sections.createStudy.guest.createStudyDescription')}
              side={option.side}
            >
              <DropdownMenuItem
                aria-disabled="true"
                className="cursor-not-allowed items-start p-3"
                onSelect={e => {
                  e.preventDefault();
                }}
              >
                <option.icon size={24} />
                <div className="flex flex-col text-sm">
                  <div className="flex items-center gap-2 font-semibold">
                    {option.title}
                    <Lock className="size-3" />
                  </div>
                  <div className="text-muted-foreground">{option.description}</div>
                </div>
              </DropdownMenuItem>
            </GuestLockedPopover>
          );
        }

        if (option.status === 'team_locked') {
          return (
            <DropdownMenuItem
              key={option.title}
              aria-disabled="true"
              className="cursor-not-allowed items-start p-3"
              onSelect={e => {
                e.preventDefault();
              }}
            >
              <option.icon size={24} />
              <div className="flex flex-col text-sm">
                <div className="flex items-center gap-2 font-semibold">
                  {option.title}
                  <Lock className="size-3" />
                </div>
                <div className="text-muted-foreground">{option.description}</div>
              </div>
            </DropdownMenuItem>
          );
        }

        if (option.status === 'upgrade_required') {
          return (
            <DropdownMenuItem
              key={option.title}
              className="group relative cursor-not-allowed items-start p-3"
              onMouseEnter={() => group.close()}
              onSelect={e => {
                e.preventDefault();
                group.toggle(option.title);
              }}
            >
              <option.icon size={24} />
              <div className="flex flex-col text-sm">
                <div className="flex items-center gap-2 font-semibold">
                  {option.title}
                  <Lock className="size-3" />
                </div>
                <div className="text-muted-foreground">{option.description}</div>
              </div>
              <UpgradePopover isPopoverOpen={group.openId === option.title} side={option.side} />
            </DropdownMenuItem>
          );
        }

        if (option.status === 'coming_soon') {
          return (
            <DropdownMenuItem key={option.title} disabled className="items-start p-3">
              <option.icon size={24} />
              <div className="flex flex-col text-sm">
                <div className="flex items-center gap-2 font-semibold">
                  {option.title}
                  <div className="whitespace-nowrap rounded-sm bg-foreground px-1 py-0 text-xs font-semibold leading-4 text-background">
                    {t('sections.studies.comingSoon')}
                  </div>
                </div>
                <div className="text-muted-foreground">{option.description}</div>
              </div>
            </DropdownMenuItem>
          );
        }

        if (option.href && !option.onClick) {
          return (
            <DropdownMenuItem key={option.title} asChild className="items-start p-3">
              <a href={option.href}>
                <option.icon size={24} />
                <div className="flex flex-col text-sm">
                  <div className="font-semibold">{option.title}</div>
                  <div className="text-muted-foreground">{option.description}</div>
                </div>
              </a>
            </DropdownMenuItem>
          );
        }

        return (
          <DropdownMenuItem
            key={option.title}
            onClick={e => {
              e.stopPropagation();
              option.onClick?.();
            }}
            className="items-start p-3"
          >
            <option.icon size={24} />
            <div className="flex flex-col text-sm">
              <div className="font-semibold">{option.title}</div>
              <div className="text-muted-foreground">{option.description}</div>
            </div>
          </DropdownMenuItem>
        );
      })}
      {!hideProjectOptions && (
        <>
          <MenuLabel>{t('sections.createStudy.project.title')}</MenuLabel>
          {projectOptions.map(option => {
            if (option.status === 'guest_locked') {
              return (
                <GuestLockedPopover
                  key={option.title}
                  description={t('sections.createStudy.guest.createStudyDescription')}
                  side={option.side === 'left' ? 'left' : 'right'}
                >
                  <DropdownMenuItem
                    className="cursor-not-allowed items-start p-3"
                    onSelect={e => {
                      e.preventDefault();
                    }}
                  >
                    <option.icon size={24} />
                    <div className="flex flex-col text-sm">
                      <div className="flex items-center gap-2 font-semibold">
                        {option.title}
                        <Lock className="size-3" />
                      </div>
                      <div className="text-muted-foreground">{option.description}</div>
                    </div>
                  </DropdownMenuItem>
                </GuestLockedPopover>
              );
            }

            return (
              <DropdownMenuItem key={option.title} asChild className="items-start p-3">
                <a href={option.href ?? '/'} {...(option.newTab ? { target: '_blank', rel: 'noreferrer' } : {})}>
                  <option.icon size={24} />
                  <div className="flex flex-col text-sm">
                    <div className="font-semibold">{option.title}</div>
                    <div className="text-muted-foreground">{option.description}</div>
                  </div>
                </a>
              </DropdownMenuItem>
            );
          })}
        </>
      )}
    </>
  );
};

const MobileCreateStudyContent = ({ studyOptions, projectOptions, hideProjectOptions }: ContentProps) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col">
      <MenuLabel>{t('sections.createStudy.studies.title')}</MenuLabel>
      {studyOptions.map(option => (
        <CreateStudyItem key={option.title} {...option} />
      ))}
      {!hideProjectOptions && (
        <>
          <MenuLabel>{t('sections.createStudy.project.title')}</MenuLabel>
          {projectOptions.map(option => (
            <CreateStudyItem key={option.title} {...option} />
          ))}
        </>
      )}
    </div>
  );
};

export const CreateStudy = ({
  trigger,
  title,
  isCollapsed = false,
  align = 'end',
  side = 'bottom',
  popoverSide = 'right',
  contentClassName = 'overflow-visible p-3',
  projectId,
}: CreateStudyProps = {}) => {
  const { t } = useTranslation();
  const { details } = useConnectedClient();
  const isResearcher = details?.type?.researcher ?? false;
  const isGuest = useIsGuest();
  const [isMounted, setIsMounted] = useState(false);
  const isMobile = useMediaQuery('(max-width: 639px)');
  const {
    studyOptions,
    projectOptions,
    isSetupOpen,
    setupOpenCount,
    handleSetupComplete,
    handleCustomSetup,
    handleSetupClose,
    handleSetupError,
    isProjectDialogOpen,
    setIsProjectDialogOpen,
    handleProjectSelect,
  } = useCreateStudyOptions({ popoverSide, projectId, isResearcher, isGuest });

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const defaultTitle = t('sections.createStudy.title');

  const defaultTrigger = (
    <Button variant="primary" data-testid="create-study-button">
      <Plus className="size-4" />
      {defaultTitle}
    </Button>
  );

  return (
    <>
      <ResponsiveMenu
        id="create-study-dropdown"
        trigger={trigger ?? defaultTrigger}
        title={title ?? defaultTitle}
        align={align}
        side={side}
        isCollapsed={isCollapsed}
        contentClassName={contentClassName}
      >
        <LockedPopoverGroup>
          {isMounted && isMobile ? (
            <MobileCreateStudyContent
              studyOptions={studyOptions}
              projectOptions={projectOptions}
              hideProjectOptions={!!projectId}
            />
          ) : (
            <DesktopCreateStudyContent
              studyOptions={studyOptions}
              projectOptions={projectOptions}
              hideProjectOptions={!!projectId}
            />
          )}
        </LockedPopoverGroup>
      </ResponsiveMenu>

      {setupOpenCount > 0 ? (
        // Each open resets the boundary, so the wizard can recover from a render error. `React.lazy` keeps a failed
        // import, so the toast offers a refresh. `fallbackRender`, because `fallback={null}` makes the boundary rethrow.
        <ErrorBoundary fallbackRender={() => null} onError={handleSetupError} resetKeys={[setupOpenCount]}>
          <Suspense fallback={null}>
            <AIModeratedSetup
              isOpen={isSetupOpen}
              onClose={handleSetupClose}
              onComplete={handleSetupComplete}
              onCustomSetup={handleCustomSetup}
            />
          </Suspense>
        </ErrorBoundary>
      ) : null}

      <AddToProjectDialog
        isOpen={isProjectDialogOpen}
        onOpenChange={setIsProjectDialogOpen}
        onProjectSelect={handleProjectSelect}
      />
    </>
  );
};
